import { type Entity, type OptIns, type Subject } from '~/domains/platform/infra/deus-ex-machina/ports'

import { kycAssessment } from './kyc-assessment'
import { type KycRouteKind, type KycRoutePrefs } from './kyc-assessment-routing'
import { kycFormatAssessmentStatusData } from './kyc-format-assessment-status-data'
import { type kycRules } from './kyc-rules'
import { kycStatus } from './kyc-status'

/**
 * @deprecated Legacy KYC routing for bff/entity sessions (deus-ex-machina getExMachinaSource).
 * Remove together with the legacy session source once every user is on the One Stone session.
 */
export interface ResolveLegacyKycRouteOptions {
  subject: Subject<OptIns>
  entity: Entity<OptIns>
  rules: ReturnType<typeof kycRules.kycIdentityRules>
  currentPathname: string
  prefs: KycRoutePrefs
}

export function resolveLegacyKycRoute(options: ResolveLegacyKycRouteOptions): { kind: KycRouteKind } {
  const { subject, entity, rules, currentPathname, prefs } = options

  const {
    kycRequest: userKycRequest,
    unsafe: { entitiesKycRequests }
  } = subject

  const emailVerified = subject.emailVerified
  const entityRejected = entity.approvalStatus === 'rejected'

  const assessmentStatus = kycFormatAssessmentStatusData(entity, userKycRequest, entitiesKycRequests)
  const { updateAssessment } = kycStatus.assessmentStatus(assessmentStatus)

  const updateAssessmentReason =
    userKycRequest?.reason === 'update' ||
    Boolean(kycAssessment.findPendingOrganizationAssessment('update', entity.id, entitiesKycRequests))
  const unblockChallengeAssessment = userKycRequest?.reason === 'unblock_challenge'
  const deviceRegistrationAssessment =
    userKycRequest?.reason === 'device_registration' ||
    Boolean(kycAssessment.findPendingOrganizationAssessment('device_registration', entity.id, entitiesKycRequests))
  const unblockChallengeAssessmentRequestedStatus = userKycRequest?.status === 'requested'

  const { hasPendencies, hasNoAvailableIdentities, hasAcquirerVision, riskAssessment, hasBankingVision } = rules

  const isUpdateAssessmentCase =
    updateAssessmentReason && !prefs.dontShowKycUpdateAssessment.includes(updateAssessment?.id as string)

  const hasNoAccountAndEmailIsVerified = !hasBankingVision && !hasAcquirerVision && emailVerified && hasPendencies

  const isIdentityRejected = !hasAcquirerVision && emailVerified && entityRejected

  if (
    userKycRequest &&
    unblockChallengeAssessment &&
    unblockChallengeAssessmentRequestedStatus &&
    !prefs.dontShowChallengeBlocked.includes(subject.id)
  ) {
    return { kind: 'challenge-blocked' }
  }

  if (riskAssessment) return { kind: 'risk-blocked' }

  if (isUpdateAssessmentCase || hasNoAccountAndEmailIsVerified || isIdentityRejected || deviceRegistrationAssessment) {
    return { kind: 'analysis' }
  }

  if (!emailVerified && hasPendencies) return { kind: 'confirm-email' }

  if (hasNoAvailableIdentities && currentPathname === '/home') return { kind: 'select-account' }

  return { kind: 'render' }
}
